import { useEffect, useMemo, useState } from 'react';
import { Pagination, Stack, Text } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import type { Answer } from 'api/requests/communityBuzz/interfaces';
import { SocialSentiment } from 'api/requests/socialSentiments/interfaces';
import { EntryDetails } from '../EntryDetails/EntryDetails';
import {
  AnswerFormData,
  QAEntryModal,
} from '../../CommunityBuzz/QAEntryModal/QAEntryModal';
import { StyledButton, StyledModal } from './components';
import {
  ANSWERS_PER_PAGE_ON_DESKTOP,
  ANSWERS_PER_PAGE_ON_MOBILE,
} from '../constants';
import { ChatBubbleOutline } from '@mui/icons-material';
import { AnswerDetails } from '../AnswerDetails/AnswerDetails';
import { SOCIAL_SENTIMENT_GA4_EVENTS } from '../constants';
import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';

interface CADisplayModalProps {
  open: boolean;
  onClose: () => void;
  question?: SocialSentiment;
  onSubmitAnswer: (data: AnswerFormData) => Promise<void> | void;
}

export const CADisplayModal = ({
  open,
  onClose,
  question,
  onSubmitAnswer,
}: CADisplayModalProps) => {
  const { isMobile } = useScreenSize();
  const { trackEventGA4 } = useAppConfigContext();
  const [openCAEntryModal, setOpenCAEntryModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const answersLength = question?.comments?.length || 0;
  const answersPerPage = isMobile
    ? ANSWERS_PER_PAGE_ON_MOBILE
    : ANSWERS_PER_PAGE_ON_DESKTOP;

  const count = useMemo(
    () => Math.ceil(answersLength / answersPerPage),
    [answersLength, answersPerPage],
  );
  const paginatedAnswers = useMemo(
    () =>
      question?.comments?.slice(
        (currentPage - 1) * answersPerPage,
        currentPage * answersPerPage,
      ) || [],
    [question?.comments, currentPage, answersPerPage],
  );
  const showPagination = useMemo(
    () => answersLength > answersPerPage,
    [answersLength, answersPerPage],
  );

  // Reset currentPage when question changes or when answersLength becomes less than currentPage
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(answersLength / answersPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(1);
    }
  }, [answersLength, answersPerPage, currentPage]);

  // Reset currentPage when question changes
  useEffect(() => {
    setCurrentPage(1);
  }, [question?.id]);

  useEffect(() => {
    if (!open) {
      setOpenCAEntryModal(false);
    }
  }, [open]);

  const handlePageChange = (e: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleOpenQAEntryModal = () => {
    trackEventGA4({
      event_name:
        SOCIAL_SENTIMENT_GA4_EVENTS.socialSentimentAddCommentAction.name,
      type: SOCIAL_SENTIMENT_GA4_EVENTS.socialSentimentAddCommentAction.type,
    });
    setOpenCAEntryModal(true);
  };

  const handleCloseQAEntryModal = () => {
    setOpenCAEntryModal(false);
  };

  const handleSubmit = (data: AnswerFormData) => onSubmitAnswer(data);

  const handleCloseModal = () => {
    setOpenCAEntryModal(false);
    onClose();
  };

  return (
    <StyledModal
      open={open}
      onClose={handleCloseModal}
      hideBackdrop
      hasCloseIcon
    >
      <Stack
        sx={{
          height: '100%',
          overflowY: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
          pb: { xs: 4, md: 3 },
        }}
      >
        <EntryDetails item={question} isModal />
        <StyledButton variant="outlined" onClick={handleOpenQAEntryModal}>
          Join the conversation
        </StyledButton>
        {paginatedAnswers?.map((comment, index: number) => {
          // Map SocialSentimentComment to Answer format
          const answer: Answer = {
            id: comment.id,
            body_md: comment.body_md,
            username: comment.username,
            source: comment.source,
            created_at: comment.created_at,
            views: comment.views,
          };
          return <AnswerDetails key={comment.id ?? index} answer={answer} />;
        })}
        {showPagination && (
          <Stack mt={2}>
            <Pagination
              count={count}
              page={currentPage}
              shape="circular"
              onChange={handlePageChange}
              showFirstButton
              showLastButton
              size={isMobile ? 'small' : 'medium'}
              siblingCount={isMobile ? 0 : 2}
              sx={{
                '&.MuiPagination-root': {
                  display: 'flex',
                  justifyContent: 'center',
                },
              }}
            />
          </Stack>
        )}
        {paginatedAnswers?.length === 0 && (
          <Stack textAlign="center" justifyContent="center" alignItems="center">
            <ChatBubbleOutline
              sx={{ color: 'text.disabled', fontSize: '3rem', my: 2 }}
            />
            <Text variant="h5">No answers yet</Text>
            <Text variant="body2" color="text.secondary">
              Join in and start the discussion!
            </Text>
          </Stack>
        )}
        <QAEntryModal
          isOpen={openCAEntryModal}
          onClose={handleCloseQAEntryModal}
          onSubmit={handleSubmit}
          title="Write a Comment"
          type="Comment"
        />
      </Stack>
    </StyledModal>
  );
};
